import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '@/components/organisms/Header';
import { Sidebar } from '@/components/organisms/Sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { ROUTES } from '@/constants/routes';
import type { ReactNode } from 'react';
import type { NavItem } from '@/components/organisms/Sidebar';
import type { UserRole } from '@/types/auth.types';
import styles from './AppLayout.module.scss';

export interface AppLayoutProps {
  children: ReactNode;
  navItems: NavItem[];
  /**
   * Área a la que pertenece el layout (empresa / backoffice / alumno). Define
   * las rutas de perfil/ajustes/notificaciones del Header. Se pasa explícito
   * desde cada layout para que un usuario dual (alumno + admin) en el área de
   * EMPRESA vaya a las pantallas de empresa, no a las de alumno (su `role`
   * sigue siendo 'student'). Si no se indica, cae a `user.role`.
   */
  navRole?: UserRole;
}

// Top-level create/edit pages that activate the compact WordPress-style editor layout.
const EDITOR_PAGE_PATTERN =
  /^\/backoffice\/(stories|resources|conversations)\/(create|[^/]+\/edit)$/;

// All editor sub-pages that hide header and sidebar entirely (superset of EDITOR_PAGE_PATTERN).
const EDITOR_FULLSCREEN_PATTERNS = [
  /^\/backoffice\/(stories|resources|conversations)\/(create|[^/]+\/edit)$/,
  /^\/backoffice\/resources\/[^/]+\/content(\/(new|[^/]+\/edit))?$/,
  /^\/backoffice\/resources\/[^/]+\/activities(\/(new|[^/]+\/edit))?$/,
  /^\/backoffice\/conversations\/[^/]+\/content(\/(new|[^/]+\/edit))?$/,
  /^\/backoffice\/conversations\/[^/]+\/activities(\/(new|[^/]+\/edit))?$/,
  /^\/backoffice\/stories\/[^/]+\/resources(\/(new|[^/]+\/edit))?$/,
  /^\/backoffice\/stories\/[^/]+\/activities(\/(new|[^/]+\/edit))?$/,
];

function checkEditorFullscreen(pathname: string): boolean {
  return EDITOR_FULLSCREEN_PATTERNS.some((p) => p.test(pathname));
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

const ROLE_NAV_ROUTES = {
  student: {
    profile: ROUTES.STUDENT.PROFILE,
    settings: ROUTES.STUDENT.SETTINGS,
    notifications: ROUTES.STUDENT.NOTIFICATIONS,
  },
  company_admin: {
    profile: ROUTES.COMPANY.PROFILE,
    settings: ROUTES.COMPANY.SETTINGS,
    notifications: ROUTES.COMPANY.NOTIFICATIONS,
  },
  backoffice: {
    profile: ROUTES.BACKOFFICE.PROFILE,
    settings: ROUTES.BACKOFFICE.SETTINGS,
    notifications: ROUTES.BACKOFFICE.NOTIFICATIONS,
  },
} as const;

export function AppLayout({ children, navItems, navRole }: AppLayoutProps) {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isEditorPage = EDITOR_PAGE_PATTERN.test(location.pathname);
  const isEditorFullscreen = checkEditorFullscreen(location.pathname);

  const handleRoleNavigate = (section: 'profile' | 'settings' | 'notifications') => {
    // El área (navRole) manda; si no se indica, cae al rol del usuario. Esto
    // hace que un dual (alumno + admin) en el área de empresa vaya a las
    // pantallas de empresa, no a las de alumno.
    const role = navRole ?? user?.role;
    if (role) navigate(ROLE_NAV_ROUTES[role][section]);
  };

  return (
    <div className={styles.appLayout}>
      {!isEditorFullscreen && (
        <>
          <Header
            userName={user?.name}
            userAvatar={user?.avatar}
            userInitials={user?.name ? getInitials(user.name) : 'U'}
            notificationCount={unreadCount}
            onLogout={logout}
            onProfileClick={() => handleRoleNavigate('profile')}
            onSettingsClick={() => handleRoleNavigate('settings')}
            onNotificationsClick={() => handleRoleNavigate('notifications')}
          />
          <Sidebar
            navItems={navItems}
            activePath={location.pathname}
            onNavigate={navigate}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        </>
      )}
      <main
        className={styles.appLayout__main}
        data-editor={isEditorPage ? 'true' : undefined}
        data-fullscreen-editor={isEditorFullscreen ? 'true' : undefined}
      >
        {children}
      </main>
    </div>
  );
}

export default AppLayout;
